import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';

import { cn } from '~ui/lib/utils';

/* shadcn/ReUI-style Button mapped onto the Phase-1 CSS vars.
   No preflight in this app — border utilities always pair with border-solid.
   Legacy `~co/common/button` parity: `as`/`href` polymorphism, `accent`,
   `shape`, `block`, and the flat/dotted/active/accent variants. */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-[length:var(--primary-font-size)] font-medium outline-none transition-[color,background-color,filter] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 focus-visible:ring-2 focus-visible:ring-ring',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:brightness-110 active:brightness-90',
        primary:
          'bg-primary text-primary-foreground hover:brightness-110 active:brightness-90',
        destructive:
          'bg-destructive text-destructive-foreground hover:brightness-110 active:brightness-90',
        outline:
          'border border-solid border-input bg-background text-foreground hover:bg-hover',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-hover active:bg-active',
        ghost: 'text-foreground hover:bg-hover active:bg-active',
        link: 'text-accent underline-offset-4 hover:underline',
        //legacy `~co/common/button` lookalikes
        flat: 'bg-secondary text-secondary-foreground hover:bg-hover active:bg-active',
        accent: 'text-accent font-semibold hover:bg-hover active:bg-active',
        active: 'bg-active text-accent font-semibold cursor-default',
        dotted:
          'border border-dashed border-input bg-background text-foreground hover:border-input',
      },
      size: {
        default: 'h-[var(--button-height)] px-3',
        sm: 'h-[0.6rem] px-2 text-[length:var(--tertiary-font-size)]',
        small:
          'h-[0.6rem] px-2 text-[length:var(--tertiary-font-size)]',
        lg: 'h-[0.8rem] px-4',
        icon: 'size-[var(--button-height)]',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

const Button = React.forwardRef(function Button(
  {
    className,
    variant,
    size,
    asChild = false,
    as,
    accent,
    shape,
    block,
    onKeyDown,
    ...props
  },
  ref,
) {
  const Comp = asChild ? Slot : props.href ? 'a' : as || 'button';
  const isNativeButton = Comp === 'button';

  return (
    <Comp
      ref={ref}
      type={isNativeButton ? 'button' : undefined}
      role={isNativeButton || asChild ? undefined : 'button'}
      tabIndex={
        !isNativeButton && !asChild ? (props.disabled ? '-1' : '0') : undefined
      }
      className={cn(
        buttonVariants({ variant, size }),
        `ui-btn-${variant || 'default'}`,
        className,
      )}
      data-accent={accent || undefined}
      data-shape={shape || undefined}
      data-block={block || undefined}
      {...props}
      onKeyDown={
        isNativeButton || asChild
          ? onKeyDown
          : (e) => {
              if (
                e.key === 'Enter' &&
                e.currentTarget.getAttribute('disabled') == null
              ) {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.click();
              } else if (onKeyDown) onKeyDown(e);
            }
      }
    />
  );
});

export { Button, buttonVariants };
